import type { RouteRecordRaw } from 'vue-router'

import type { PaymentMethodCode } from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import { ROLES } from '@backmarket/nuxt-layer-buyback/components/OrderConversation/OrderConversation.constants'
import { PaymentFlow } from '@backmarket/nuxt-layer-payment/pages/config/flows'
import { getRedirectOnPaymentDomainMiddleware } from '@backmarket/nuxt-layer-payment/pages/navigation/getRedirectOnPaymentDomainMiddleware'

import { ROUTE_NAMES } from '~/constants/routes'
import OptionsTranslations from '~/scopes/buyback/pages/options/Options.translations'
import OrdersTranslations from '~/scopes/buyback/pages/orders/Orders.translations'

const routes = (): RouteRecordRaw[] => {
  const routesConfig: RouteRecordRaw[] = [
    {
      name: ROUTE_NAMES.BUYBACK.ORDERS.INDEX,
      component: () => import('~/scopes/buyback/pages/orders/Orders.vue'),
      path: '/bo-seller/buyback/orders',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'orders',
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
      children: [
        {
          name: ROUTE_NAMES.BUYBACK.ORDERS.ACTIVE,
          component: () =>
            import('~/scopes/buyback/pages/orders/components/Tab/Tab.vue'),
          path: 'active',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'orders.active',
            tabItem: {
              label: OrdersTranslations.newOrders,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
        {
          name: ROUTE_NAMES.BUYBACK.ORDERS.PENDING,
          component: () =>
            import('~/scopes/buyback/pages/orders/components/Tab/Tab.vue'),
          path: 'pending',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'orders.pending',
            tabItem: {
              label: OrdersTranslations.pendingCustomers,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
        {
          name: ROUTE_NAMES.BUYBACK.ORDERS.ALL,
          component: () =>
            import('~/scopes/buyback/pages/orders/components/Tab/Tab.vue'),
          path: 'all',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'orders.all',
            tabItem: {
              label: OrdersTranslations.allOrders,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
      ],
    },
    {
      name: ROUTE_NAMES.BUYBACK.ORDERS.DETAILS,
      component: () =>
        import('~/scopes/buyback/pages/orderDetails/OrderDetails.vue'),
      path: '/bo-seller/buyback/orders/:id',
      meta: {
        isBuyback: true,
        disableClientSideNavigation: () => true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.ORDERS.CONVERSATION,
      path: '/bo-seller/buyback/orders/:id/conversation',
      component: () =>
        import('@backmarket/nuxt-layer-buyback/components/OrderConversation/OrderConversation.vue'),
      props: {
        role: ROLES.MERCHANT,
      },
      meta: {
        layout: 'buyback-conversation',
        isBuyback: true,
        disableClientSideNavigation: () => true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.CREATE_QUOTATION,
      component: () =>
        import('~/scopes/buyback/pages/quotations/CreateProductQuotations.vue'),
      path: '/bo-seller/buyback/create-quotation/:productId?',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'create-quotation',
        owners: ['bot-squad-circularity-offer-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
      component: () =>
        import('~/scopes/buyback/pages/search-product/SearchProduct.vue'),
      path: '/bo-seller/buyback/search-product',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'search-product',
        owners: ['bot-squad-circularity-offer-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.WISHLIST,
      component: () => import('~/scopes/buyback/pages/wishlist/Wishlist.vue'),
      path: '/bo-seller/buyback/wishlist',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'wishlist',
        owners: ['bot-squad-circularity-offer-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.OPPORTUNITIES,
      component: () =>
        import('~/scopes/buyback/pages/opportunities/Opportunities.vue'),
      path: '/bo-seller/buyback/opportunities',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'opportunities',
        owners: ['bot-squad-circularity-offer-front'],
      },
    },
    {
      name: ROUTE_NAMES.BUYBACK.OPTIONS.INDEX,
      component: () => import('~/scopes/buyback/pages/options/Options.vue'),
      redirect: { name: ROUTE_NAMES.BUYBACK.OPTIONS.ADDRESSES },
      path: '/bo-seller/buyback/options',
      meta: {
        isBuyback: true,
        autonomousBannerPageName: 'options',
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
      children: [
        {
          name: ROUTE_NAMES.BUYBACK.OPTIONS.ADDRESSES,
          component: () =>
            import('~/scopes/buyback/pages/options/components/Addresses/Addresses.vue'),
          path: 'addresses',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'options.addresses',
            tabItem: {
              label: OptionsTranslations.adressesTabTitle,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
        {
          name: ROUTE_NAMES.BUYBACK.OPTIONS.PAYMENT,
          component: () =>
            import('~/scopes/buyback/pages/options/components/Payment/Payment.vue'),
          path: 'payment',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'options.payment',
            tabItem: {
              label: OptionsTranslations.paymentTabTitle,
            },
            owners: ['bot-squad-circularity-order-lifecycle-front'],
          },
        },
        {
          name: ROUTE_NAMES.BUYBACK.OPTIONS.PRICING_RULES,
          component: () =>
            import('~/scopes/buyback/pages/options/components/PricingRules/PricingRules.vue'),
          path: 'pricing-rules',
          meta: {
            isBuyback: true,
            autonomousBannerPageName: 'options.pricing-rules',
            tabItem: {
              label: OptionsTranslations.pricingRulesTabTitle,
            },
            owners: ['bot-squad-circularity-offer-front'],
          },
        },
      ],
    },
    {
      name: ROUTE_NAMES.BUYBACK.OPTIONS.UPDATE_PAYMENT_METHOD,
      component: () =>
        import('~/scopes/buyback/pages/update-payment-method/UpdatePaymentMethod.vue'),
      path: '/bo-seller/buyback/options/payment/:code',
      meta: {
        middleware: getRedirectOnPaymentDomainMiddleware(
          PaymentFlow.BUYBACK_SELLER_UPDATE_PAYMENT_METHOD,
          (to) => ({ code: to.params.code as PaymentMethodCode }),
        ),
        isBuyback: true,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    },
  ]

  return routesConfig
}

export default routes
